/**
 * Página de Gestión de Usuarios (solo ADMIN)
 *
 * CRUD completo de usuarios del sistema con asignación de roles.
 */

import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../components/contexts/AuthContext';
import Navbar from '../components/layout/Navbar';
import EntityTable, { renderActionButton } from '../components/common/EntityTable';
import HighlightedText from '../components/common/HighlightedText';
import { useExportExcel } from '../hooks/useExportExcel';
import EntityModal from '../components/common/EntityModal';
import FilterPanel from '../components/common/FilterPanel';
import Pagination from '../components/common/Pagination';
import ConfirmDialog from '../components/common/ConfirmDialog';
import usePaginatedEntity from '../hooks/usePaginatedEntity';
import apiService from '../services/apiService';
import Breadcrumb from '../components/common/Breadcrumb';
import ROUTES from '../constants/routes';
import { validateEmail } from '../utils/sanitization';

/**
 * Página de administración de usuarios del sistema
 *
 * @returns {JSX.Element}
 */
function GestionUsuarios() {
  const { hasPermission } = useAuth();
  const [roles, setRoles] = useState([]);

  useEffect(() => {
    apiService.getRoles().then(data => {
      if (data.success) setRoles(data.data);
    }).catch(err => console.warn('Error al cargar roles:', err));
  }, []);

  const {
    data,
    rawData,
    loading,
    pagination,
    filters,
    modalOpen,
    editingItem,
    confirmDialog,
    handleFilterChange,
    handlePageChange,
    handleLimitChange,
    handleCreate,
    handleEdit,
    handleDelete,
    confirmDelete,
    handleSave,
    closeModal,
    closeConfirm
  } = usePaginatedEntity({
    entityName: 'usuario',
    entityNamePlural: 'usuarios',
    fetchFn: apiService.getUsuarios,
    createFn: apiService.createUsuario,
    updateFn: apiService.updateUsuario,
    deleteFn: apiService.deleteUsuario,
    filterKeys: ['search', 'rol_id', 'activo'],
    messages: {
      createSuccess: 'Usuario creado',
      updateSuccess: 'Usuario actualizado',
      deleteSuccess: 'Usuario eliminado exitosamente'
    }
  });

  const currentEmail = apiService.getUserEmail();

  const exportToExcel = useExportExcel({
    data: rawData,
    mapFn: (u) => ({
      'Nombre': u.nombre,
      'Email': u.email,
      'Rol': u.rol?.nombre || '',
      'Activo': u.activo ? 'Sí' : 'No',
      'Verificado': u.email_verificado ? 'Sí' : 'No'
    }),
    sheetName: 'Usuarios',
    columnWidths: [{ wch: 25 }, { wch: 30 }, { wch: 15 }, { wch: 8 }, { wch: 10 }],
    entityLabel: 'usuarios'
  });

  const renderUsuarioMobileCard = useCallback((row, index, visibleActions) => (
    <div key={row.id || index} className="mobile-card">
      <div className="mobile-card-header">
        <span className="mobile-card-title">
          <HighlightedText text={row.nombre || ''} searchTerm={filters.search} />
        </span>
        <span className={`badge ${row.activo ? 'badge-success' : 'badge-warning'}`}>
          {row.activo ? 'Activo' : 'Inactivo'}
        </span>
      </div>
      <div className="mobile-card-body">
        <div className="mobile-card-field">
          <span className="mobile-card-label">Email</span>
          <span className="mobile-card-value">
            <HighlightedText text={row.email || ''} searchTerm={filters.search} />
          </span>
        </div>
        <div className="mobile-card-field">
          <span className="mobile-card-label">Rol</span>
          <span className="mobile-card-value">
            <span className="badge badge-info">{row.rol?.nombre || '—'}</span>
          </span>
        </div>
        <div className="mobile-card-field">
          <span className="mobile-card-label">Verificado</span>
          <span className="mobile-card-value">
            <span className={`badge ${row.email_verificado ? 'badge-success' : 'badge-warning'}`}>
              {row.email_verificado ? 'Sí' : 'No'}
            </span>
          </span>
        </div>
      </div>
      {visibleActions?.length > 0 && (
        <div className="card-actions">
          {visibleActions.map(action =>
            renderActionButton(action, row, action.className?.includes('danger') ? 'btn-table-action' : 'btn-sm')
          )}
        </div>
      )}
    </div>
  ), [filters.search]);

  const columns = useMemo(() => [
    { key: 'nombre', label: 'Nombre', sortable: true, highlightable: true, title: true },
    { key: 'email', label: 'Email', sortable: true, highlightable: true },
    {
      key: 'rol',
      label: 'Rol',
      render: (row) => (
        <span className="badge badge-info">{row.rol?.nombre || '—'}</span>
      )
    },
    {
      key: 'activo',
      label: 'Estado',
      render: (row) => (
        <span className={`badge ${row.activo ? 'badge-success' : 'badge-warning'}`}>
          {row.activo ? 'Activo' : 'Inactivo'}
        </span>
      )
    },
    {
      key: 'email_verificado',
      label: 'Verificado',
      render: (row) => (
        <span className={`badge ${row.email_verificado ? 'badge-success' : 'badge-warning'}`}>
          {row.email_verificado ? 'Sí' : 'No'}
        </span>
      )
    },
    { key: 'acciones', label: 'Acciones', align: 'center' }
  ], []);

  const modalCreateFields = useMemo(() => [
    { name: 'nombre', label: 'Nombre', type: 'text', required: true, maxLength: 100 },
    { name: 'email', label: 'Email', type: 'text', required: true, validators: [validateEmail] },
    { name: 'password', label: 'Contraseña', type: 'password', required: true },
    {
      name: 'rol_id',
      label: 'Rol',
      type: 'select',
      required: true,
      options: roles.map(r => ({ value: r.id, label: r.nombre }))
    }
  ], [roles]);

  const modalEditFields = useMemo(() => [
    { name: 'nombre', label: 'Nombre', type: 'text', required: true, maxLength: 100 },
    {
      name: 'rol_id',
      label: 'Rol',
      type: 'select',
      required: true,
      options: roles.map(r => ({ value: r.id, label: r.nombre }))
    },
    { name: 'activo', label: 'Activo', type: 'checkbox' }
  ], [roles]);

  const filterConfig = useMemo(() => [
    { name: 'search', type: 'text', placeholder: 'Buscar por nombre o email...', icon: 'fa-search' },
    {
      name: 'rol_id',
      type: 'select',
      placeholder: 'Todos los roles',
      options: roles.map(r => ({ value: r.id, label: r.nombre }))
    },
    {
      name: 'activo',
      type: 'select',
      placeholder: 'Todos los estados',
      options: [
        { value: '1', label: 'Activo' },
        { value: '0', label: 'Inactivo' }
      ]
    }
  ], [roles]);

  const actions = [
    {
      icon: 'fa-edit',
      className: 'btn-ghost-primary',
      title: 'Editar',
      onClick: handleEdit,
      show: () => hasPermission('usuarios.update')
    },
    {
      icon: 'fa-trash',
      className: 'btn-ghost-danger',
      title: 'Eliminar',
      onClick: handleDelete,
      show: () => hasPermission('usuarios.delete'),
      disabled: (row) => row.email === currentEmail
    }
  ];

  return (
    <>
      <Navbar />
      <div className="main-content">
        <Breadcrumb items={[
          { label: 'Inicio', to: ROUTES.HOME },
          { label: 'Usuarios' }
        ]}>
          {hasPermission('roles.read') && (
            <Link to={ROUTES.GESTION_ROLES} className="btn btn-secondary">
              <i className="fas fa-user-shield"></i> Gestión de Roles
            </Link>
          )}
        </Breadcrumb>

        <FilterPanel
          filters={filters}
          onFilterChange={handleFilterChange}
          filterConfig={filterConfig}
          actionConfig={[
            { label: 'Exportar Excel', icon: 'fa-file-excel', variant: 'secondary', onClick: exportToExcel },
            { label: 'Nuevo Usuario', icon: 'fa-plus', variant: 'primary', onClick: handleCreate, show: () => hasPermission('usuarios.create') }
          ]}
        />

        <EntityTable
          columns={columns}
          data={data}
          loading={loading}
          searchTerm={filters.search}
          actions={actions}
          emptyMessage="No se encontraron usuarios"
          animateRows={true}
          mobileCardRenderer={renderUsuarioMobileCard}
        />

        <Pagination
          currentPage={pagination.page}
          totalItems={pagination.total}
          itemsPerPage={pagination.limit}
          onPageChange={handlePageChange}
          onItemsPerPageChange={handleLimitChange}
          loading={loading}
        />

        <EntityModal
          isOpen={modalOpen}
          onClose={closeModal}
          onSave={handleSave}
          title={editingItem ? `Editar Usuario: ${editingItem.nombre}` : 'Nuevo Usuario'}
          fields={editingItem ? modalEditFields : modalCreateFields}
          initialData={editingItem}
          loading={loading}
        />

        <ConfirmDialog
          isOpen={confirmDialog.open}
          onClose={closeConfirm}
          onConfirm={confirmDelete}
          title="Eliminar Usuario"
          message={`¿Estás seguro de que deseas eliminar al usuario "${confirmDialog.item?.nombre}"?`}
          confirmText="Eliminar"
          variant="danger"
        />
      </div>
    </>
  );
}

export default GestionUsuarios;
